import { Link } from "react-router-dom";
import { type AdminMember, type AdminOrgDetail, type OrgRole } from "@currencycore/shared";
import { setMemberRole } from "../lib/api.ts";
import { Select } from "./Select.tsx";
import { useConfirm } from "./useConfirm.tsx";
import { toast } from "./Toast.tsx";
import { errMsg } from "../lib/ui.ts";

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
];

/**
 * Member list with role editing. An owner's role is LOCKED — to change it you
 * promote another member to owner (a transfer), which demotes the current
 * owner. Each member links to their user detail page.
 */
export function MembersList({
  orgId,
  members,
  onUpdated,
}: {
  orgId: string;
  members: AdminMember[];
  onUpdated: (d: AdminOrgDetail) => void;
}) {
  const { ask, dialog } = useConfirm();

  async function apply(uid: string, role: OrgRole) {
    try {
      const d = await setMemberRole(orgId, uid, role);
      onUpdated(d);
      toast("Role updated.", true);
    } catch (e) {
      toast(errMsg(e, "Could not change the role."));
    }
  }

  function change(m: AdminMember, role: OrgRole) {
    if (role === m.role) return;
    const isTransfer = role === "owner";
    ask({
      title: isTransfer ? "Transfer ownership?" : "Change role?",
      message: isTransfer ? (
        <>
          Make <strong>{m.name ?? m.email}</strong> the owner of this organization? The current
          owner will be demoted to admin.
        </>
      ) : (
        <>
          Change <strong>{m.name ?? m.email}</strong>'s role to <strong>{role}</strong>?
        </>
      ),
      confirmLabel: isTransfer ? "Transfer ownership" : "Change role",
      run: () => apply(m.userId, role),
    });
  }

  return (
    <section className="drawer-section">
      <div className="section-row between">
        <h4>Members</h4>
        <span className="muted">{members.length}</span>
      </div>
      <div className="member-list">
        {members.map((m) => (
          <div className="member-row" key={m.userId}>
            <span className="avatar sm">{(m.name ?? m.email)[0]?.toUpperCase()}</span>
            <Link to={`/users/${m.userId}`} className="member-meta linkable">
              <span className="name">
                {m.name ?? "—"} {m.banned && <span className="badge badge-danger">banned</span>}
              </span>
              <span className="email mono muted">{m.email}</span>
            </Link>
            {m.role === "owner" ? (
              <span className="role-locked" title="Transfer ownership to change">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="5" y="11" width="14" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                Owner
              </span>
            ) : (
              <Select value={m.role} options={ROLE_OPTIONS} onChange={(v) => change(m, v as OrgRole)} ariaLabel={`Role for ${m.email}`} />
            )}
          </div>
        ))}
      </div>
      {dialog}
    </section>
  );
}
